import { useEffect, MutableRefObject, useRef } from "react";

interface useTargetMessageEffectProps {
  targetMessageId: string | null;
  urlTargetMessageId: string | null;
  messageTheme: string | null;
  refetchTargetMessage: boolean;
  showLessonOnboarding: boolean;
  previousTargetMessageIdRef: MutableRefObject<string | null>;
  skipRefetchRef: MutableRefObject<boolean>;
  initialLoadCompletedRef: MutableRefObject<boolean>;
  previousMessageThemeRef: MutableRefObject<string | null>;
  isProcessingUrlTargetRef: MutableRefObject<boolean>;
  messagesContainerRef: MutableRefObject<HTMLDivElement | null>;
  animationTimeoutRef: MutableRefObject<number | null>;
  setWaitForAnimation: (value: boolean) => void;
  setIsSearchingMessage: (value: boolean) => void;
  cleanMessageArray: () => Promise<void>;
  fetchInitialMessages: (messageId?: string) => Promise<any>;
  updateMessageTheme: (
    newMessage: "both" | "lessons" | "chats"
  ) => Promise<string>;
  scrollToMessageWithRetry: (messageId: string, isFromUrl?: boolean) => void;
  handleMessageNotFound: (fromUrl: boolean) => void;
  messages: any[];
}

export const useTargetMessageEffect = ({
  targetMessageId,
  urlTargetMessageId,
  messageTheme,
  refetchTargetMessage,
  showLessonOnboarding,
  previousTargetMessageIdRef,
  skipRefetchRef,
  initialLoadCompletedRef,
  previousMessageThemeRef,
  messagesContainerRef,
  animationTimeoutRef,
  setWaitForAnimation,
  setIsSearchingMessage,
  cleanMessageArray,
  fetchInitialMessages,
  updateMessageTheme,
  scrollToMessageWithRetry,
  handleMessageNotFound,
  messages,
}: useTargetMessageEffectProps) => {
  const isLoadingRef = useRef(false);
  const timeoutsRef = useRef<number[]>([]);
  const lastActionTimeRef = useRef<number>(0);
  const safetyTimeoutRef = useRef<number | null>(null);
  const messageNotFoundRef = useRef(false);
  const pendingTargetMessageIdRef = useRef<string | null>(null);
  const pendingUrlTargetRef = useRef(false);

  const resetLoadingState = () => {
    isLoadingRef.current = false;
    setIsSearchingMessage(false);
    setWaitForAnimation(false);

    if (safetyTimeoutRef.current) {
      clearTimeout(safetyTimeoutRef.current);
      safetyTimeoutRef.current = null;
    }

    if (messageNotFoundRef.current) {
      messageNotFoundRef.current = false;
      const fromUrl =
        targetMessageId === urlTargetMessageId && urlTargetMessageId !== null;
      handleMessageNotFound(fromUrl);
    }
  };

  useEffect(() => {
    if (initialLoadCompletedRef.current && pendingTargetMessageIdRef.current) {
      const timeoutId = setTimeout(() => {
        const pendingId = pendingTargetMessageIdRef.current;
        const fromUrl = pendingUrlTargetRef.current;

        pendingTargetMessageIdRef.current = null;
        pendingUrlTargetRef.current = false;

        if (pendingId) {
          const exists = messages.some(
            (message) => message.message_id === pendingId
          );

          if (exists) {
            scrollToMessageWithRetry(pendingId, fromUrl);
          } else {
            processFindMessage(pendingId, fromUrl);
          }
        }
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [initialLoadCompletedRef.current, messages]);

  const processFindMessage = async (msgId: string, fromUrl: boolean) => {
    try {
      isLoadingRef.current = true;
      setIsSearchingMessage(true);

      const timeoutMs = fromUrl ? 30000 : 15000;
      safetyTimeoutRef.current = window.setTimeout(() => {
        resetLoadingState();
      }, timeoutMs);

      if (fromUrl) {
        setWaitForAnimation(true);
      }

      try {
        await updateMessageTheme("both");
      } catch (themeError) {
        messageNotFoundRef.current = true;
        resetLoadingState();
        return;
      }

      await cleanMessageArray();

      try {
        const result = await fetchInitialMessages(msgId);

        const messageFound =
          Array.isArray(result) &&
          result.some((message) => message.message_id === msgId);

        if (!messageFound) {
          messageNotFoundRef.current = true;
          resetLoadingState();
          return;
        }
      } catch (fetchError: any) {
        if (
          fetchError?.status === 404 ||
          fetchError?.response?.status === 404 ||
          fetchError?.message?.includes("not found") ||
          fetchError?.message?.includes("access denied")
        ) {
          messageNotFoundRef.current = true;
        }
        resetLoadingState();
        return;
      }

      previousMessageThemeRef.current = "both";

      await new Promise((resolve) => setTimeout(resolve, 1000));

      scrollToMessageWithRetry(msgId, fromUrl);

      const checkTimeoutId = window.setTimeout(() => {
        const messageElement = document.getElementById(`message-${msgId}`);

        if (!messageElement) {
          messageNotFoundRef.current = true;
        } else {
          scrollToMessageWithRetry(msgId, fromUrl);
        }
      }, 2000);

      timeoutsRef.current.push(checkTimeoutId);
    } catch (error) {
      resetLoadingState();
    }
  };

  useEffect(() => {
    messageNotFoundRef.current = false;

    if (safetyTimeoutRef.current) {
      clearTimeout(safetyTimeoutRef.current);
      safetyTimeoutRef.current = null;
    }

    if (isLoadingRef.current) {
      safetyTimeoutRef.current = window.setTimeout(() => {
        resetLoadingState();
      }, 15000);
      return;
    }

    const fromUrl =
      targetMessageId === urlTargetMessageId && urlTargetMessageId !== null;
    const targetChanged =
      previousTargetMessageIdRef.current !== targetMessageId;
    const currentTime = Date.now();
    const isUserAction = currentTime - lastActionTimeRef.current > 500;

    if (targetMessageId) {
      lastActionTimeRef.current = currentTime;
    }

    previousTargetMessageIdRef.current = targetMessageId;

    if (
      !targetChanged &&
      !refetchTargetMessage &&
      !showLessonOnboarding &&
      !isUserAction
    ) {
      return;
    }

    const loadMessages = async () => {
      try {
        if (!targetMessageId) {
          if (skipRefetchRef.current) {
            skipRefetchRef.current = false;
            resetLoadingState();
            return;
          }

          if (
            initialLoadCompletedRef.current &&
            previousMessageThemeRef.current === messageTheme &&
            !fromUrl
          ) {
            resetLoadingState();
            return;
          }

          isLoadingRef.current = true;

          safetyTimeoutRef.current = window.setTimeout(() => {
            resetLoadingState();
          }, 15000);

          await cleanMessageArray();

          try {
            await fetchInitialMessages();
          } catch (fetchError) {
            resetLoadingState();
            return;
          }

          previousMessageThemeRef.current = messageTheme;
          initialLoadCompletedRef.current = true;

          const timeoutId = window.setTimeout(() => {
            if (messagesContainerRef.current) {
              messagesContainerRef.current.scrollTop =
                messagesContainerRef.current.scrollHeight;
            }
            resetLoadingState();
          }, 500);

          timeoutsRef.current.push(timeoutId);
        } else {
          if (!initialLoadCompletedRef.current) {
            pendingTargetMessageIdRef.current = targetMessageId;
            pendingUrlTargetRef.current = fromUrl;
            return;
          }

          const exists = messages.some(
            (message) => message.message_id === targetMessageId
          );

          if (exists) {
            scrollToMessageWithRetry(targetMessageId, fromUrl);
            return;
          }

          processFindMessage(targetMessageId, fromUrl);
        }
      } catch (error) {
        resetLoadingState();
      }
    };

    loadMessages();

    return () => {
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current);
        safetyTimeoutRef.current = null;
      }

      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }

      timeoutsRef.current.forEach((id) => clearTimeout(id));
      timeoutsRef.current = [];

      isLoadingRef.current = false;
    };
  }, [
    targetMessageId,
    messageTheme,
    refetchTargetMessage,
    showLessonOnboarding,
  ]);
};
